// Home.js
import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';

import './Home.css';
import { useNavigate, useLocation } from 'react-router-dom';
import Post from './Post';
import CreatePostScreen from './screens/CreatePostScreen';
import api from './api';
import { usePostContext } from './context/PostContext';
import { useNotificationsContext } from './context/NotificationContext';
import BottomNav from './components/navigation/BottomNav';
import { useMessageUnreadContext } from './context/MessageUnreadContext';
import SponsoredPostCard from './components/SponsoredPostCard';
import homeMenuIcon from './assets/homemenu-icon.png';
import searchIcon from './assets/search-icon.png';
import notificationsIcon from './assets/notifications-icon.png';
import postIcon from './assets/post-icon.png';
import chatIcon from './assets/chat-icon.png';
import profileIcon from './assets/profile-icon.png';
import logo from './assets/barterfi-logo.png';
import adminIcon from './assets/admin-icon.png';
import scrollToTopIcon from './assets/scrolltotop-icon.png';
import pillScrollToTopIcon from './assets/pillscrolltotop-icon.png';
import leaderboardIcon from './assets/leaderboard-icon.png';
import receiptLogIcon from './assets/receiptlog-icon.png';
import savedIcon from './assets/save-icon-filled.png';
import settingsIcon from './assets/settings-icon.png';
import feedbackIcon from './assets/feedback-icon.png';
import supportIcon from './assets/support-icon.png';
import aboutIcon from './assets/about-icon.png';
import termsIcon from './assets/terms-icon.png';
import mywalletIcon from './assets/mywallet-icon.png';
import ordersIcon from './assets/orders-icon.png';
import saveIconFilled from './assets/save-icon-filled.png';
import StoryBubble from './components/StoryBubble';
import { ensureSelfFirst } from './utils/stories';
import { useDiscovery } from './context/DiscoveryContext';
import { hasSeenAll } from './utils/storySeen';

import RouteSlider from './components/RouteSlider';
import './components/RouteSlider.css';

import SideDrawer from './components/popups/SideDrawer';
import { useI18n } from './i18n/I18nProvider';
import LanguageSwitcher from './components/LanguageSwitcher/LanguageSwitcher';

import { SuggestedProvider } from './context/SuggestedContext';
import SuggestedRow from './components/SuggestedRow';
import NewListingsRow from './components/NewListingsRow';

import StakeDetailsDrawer from './components/popups/StakeDetailsDrawer';
import { BADGE_DISPLAY } from './constants/badges';
import { useTheme } from './context/ThemeContext';


import searchIconDark from './assets/searchdark-icon.png';
import notificationsIconDark from './assets/notificationsdark-icon.png';
import postIconDark from './assets/postdark-icon.png';
import chatIconDark from './assets/chatdark-icon.png';
import profileIconDark from './assets/profiledark-icon.png';
import homeMenuIconDark from './assets/homemenudark-icon.png';
import plansIcon from './assets/plans-icon.png';
import StakeRow from './components/StakeRow';
import dashboardIcon from './assets/dashboard-icon.png';
import FeedFilterDrawer from './components/popups/FeedFilterDrawer';
import AvatarBubbleSkeleton from './components/skeleton/AvatarBubbleSkeleton';
import StoryBubbleSkeleton from './components/skeleton/StoryBubbleSkeleton';
import SelfStoryDrawer from './components/popups/SelfStoryDrawer';
import { useSuggestedStoriesDiscovery } from './context/SuggestedStoriesDiscoveryContext';
import globeIcon from './assets/globe-icon.png';
import placeholderProfile from './assets/placeholder-profile.png';
import AccountsDrawer from './components/popups/AccountsDrawer';
import accountsIcon from './assets/accounts-icon.png';
import BarterfiPullLoader from './components/BarterfiPullLoader';
import SkeletonPost from './components/SkeletonPost';
import RisingNowCard from './components/RisingNowCard';
import SuggestedPioneersRow from './components/SuggestedPioneersRow';
import CommunityAvatarsRow from './components/CommunityAvatarsRow';
import CommunityAvatarViewer from './components/CommunityAvatarViewer';
import { canAccessStudio } from './studio/studioAccess';

import HomeAwarenessManager from './components/home/awareness/HomeAwarenessManager';
import FeedNewsDiscussionCard from './components/news/FeedNewsDiscussionCard';
import HomePioneerExplorer from './components/HomePioneerExplorer';
import pioneerSummaryIcon from './assets/pioneersummary-icon.png';


const HOME_FEED_CACHE_KEY = 'bf_home_feed_cache_v1';

const FIRST_NEWS_AFTER = 3;
const NEXT_NEWS_EVERY = 6;
const MAX_NEWS_ITEMS = 10;


/*
 * Keeps the selected News rotation stable while the current
 * JavaScript app session remains open.
 *
 * This survives Home route unmounts/remounts, but resets when
 * the browser/app is fully reloaded.
 *
 * Each account gets its own cached News rotation.
 */
const HOME_NEWS_SESSION_CACHE = new Map();

const getHomeNewsCacheKey = (user) =>
  String(
    user?._id ||
    user?.username ||
    ''
  ).toLowerCase();

const getRisingNowCardCacheKey = (username) =>
  `bf_home_rising_now_card:${String(username || '').toLowerCase()}`;

const getInitialRisingNowCard = (username) => {
  if (!username) return null;

  try {
    const raw = sessionStorage.getItem(getRisingNowCardCacheKey(username));
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    return parsed?.showCard ? parsed : null;
  } catch {
    return null;
  }
};




const HIGHLIGHT_BADGE_IDS = [
  'BARTER_KING',
  'MERCHANT_STAR',
  'CREATOR_FLAME',
  'PI_GUARDIAN',
];


function insertNewsIntoFeed(
  feedPosts = [],
  newsArticles = []
) {
  const safePosts =
    Array.isArray(feedPosts)
      ? feedPosts
      : [];

  const safeNews =
    Array.isArray(newsArticles)
      ? newsArticles.slice(
          0,
          MAX_NEWS_ITEMS
        )
      : [];

  const mixedItems = [];

  let normalPostCount = 0;
  let nextNewsIndex = 0;

  safePosts.forEach((post) => {
    if (
      post?.type ===
      'rising_now_card'
    ) {
      mixedItems.push({
        type: 'rising_now_card',
        key:
          post._id ||
          `rising-${mixedItems.length}`,
        post,
        postIndex: null,
      });

      return;
    }

    const postIndex =
      normalPostCount;

    mixedItems.push({
      type: 'post',
      key:
        post?._id ||
        `post-${postIndex}`,
      post,
      postIndex,
    });

    normalPostCount += 1;

    const reachedFirstSlot =
      normalPostCount ===
      FIRST_NEWS_AFTER;

    const reachedLaterSlot =
      normalPostCount >
        FIRST_NEWS_AFTER &&
      (
        normalPostCount -
        FIRST_NEWS_AFTER
      ) %
        NEXT_NEWS_EVERY ===
        0;

    const shouldInsertNews =
      reachedFirstSlot ||
      reachedLaterSlot;

    if (
      shouldInsertNews &&
      nextNewsIndex <
        safeNews.length
    ) {
      const article =
        safeNews[
          nextNewsIndex
        ];

      mixedItems.push({
        type: 'news',
        key:
          `news-${
            article?._id ||
            nextNewsIndex
          }`,
        article,
        newsIndex:
          nextNewsIndex,
      });

      nextNewsIndex += 1;
    }
  });

  return mixedItems;
}


function Home({ user, onUserUpdate, isActive = true }) {

  const {
    posts,
    setPosts,
    hasLoadedPosts,
    loading,
    hasMore,
    loadInitialPosts,
    fetchMorePosts,
    addPost,
    clearPostState,
    feedMode,    
  } = usePostContext();

  const { theme, toggleTheme } = useTheme();


//  const isThemeDev =
//    user?.username === 'SizweMondlane' ||
//    user?.username === 'barterfi_admin';


const isDark = theme === 'dark';

const navIcons = React.useMemo(() => ({
  search:        isDark ? searchIconDark        : searchIcon,
  notifications: isDark ? notificationsIconDark : notificationsIcon,
  post:          isDark ? postIconDark          : postIcon,
  chat:          isDark ? chatIconDark          : chatIcon,
  profile:       isDark ? profileIconDark       : profileIcon,
}), [isDark]);

const menuIcon = isDark ? homeMenuIconDark : homeMenuIcon;

const [feedFilterOpen, setFeedFilterOpen] = useState(false);

const [selfStoryDrawerOpen, setSelfStoryDrawerOpen] = useState(false);
const [selfStoryHasDbStory, setSelfStoryHasDbStory] = useState(false);


const [myApps, setMyApps] = useState([]);
const [switchingIdentity, setSwitchingIdentity] = useState(false);
const [accountsDrawerOpen, setAccountsDrawerOpen] = useState(false);

  // viewer = the logged-in user
  const viewerUsername = user?.username || (() => {
    try { return JSON.parse(localStorage.getItem('barterfi_user') || 'null')?.username; }
    catch { return null; }
  })();

  const {
    discoveryUsers,
    loading: discoveryLoading,
    error: discoveryError,
    refetch: refetchDiscovery
  } = useDiscovery();


const {
  users: suggestedUsers,
  loading: loadingSuggestedUsers,
  isFresh,
  fetchSuggested,
} = useSuggestedStoriesDiscovery();

  const [showForm, setShowForm] = useState(false);
  const [activeFilter, setActiveFilter] = useState('All');
  const [showDrawer, setShowDrawer] = useState(false);
  const [userStats, setUserStats] = useState({ followers: [], following: [] });

  const { unreadCount: unreadNotifCount, refreshUnreadCount } = useNotificationsContext();

  const { unreadDmCount } = useMessageUnreadContext();
  const [closePostMenusSignal, setClosePostMenusSignal] = useState(0);

const [showPioneerExplorer, setShowPioneerExplorer] = useState(false);
const [showPioneerFabLabel, setShowPioneerFabLabel] = useState(true);

const [showScrollTop, setShowScrollTop] = useState(false);
const [uiHidden, setUiHidden] = useState(false);

const [restoringHomeScroll, setRestoringHomeScroll] = useState(() => {
  try {
    return sessionStorage.getItem('bf_home_scrollTop') !== null;
  } catch {
    return false;
  }
});

  const [reloading, setReloading] = useState(false);
  const [showPillGate, setShowPillGate] = useState(false);

  // pull-to-refresh (icon only)
  const [pullDistance, setPullDistance] = useState(0);
  const [pullReady, setPullReady] = useState(false);
  const [pullRefreshing, setPullRefreshing] = useState(false);

  const [badgeSummary, setBadgeSummary] = useState(null);
  const [badgeSummaryLoading, setBadgeSummaryLoading] = useState(false);
  const [badgeHighlightsInjected, setBadgeHighlightsInjected] = useState(false);


const initialRisingCard = getInitialRisingNowCard(user?.username);

const [risingNowSummary, setRisingNowSummary] = useState(null);
const [risingNowCard, setRisingNowCard] = useState(initialRisingCard);
const [risingNowLoading, setRisingNowLoading] = useState(false);
const [risingHighlightsInjected, setRisingHighlightsInjected] = useState(!!initialRisingCard);

// 🆕 Premium+ spotlight pill
const [premiumSummary, setPremiumSummary] = useState(null);
const [premiumSummaryLoading, setPremiumSummaryLoading] = useState(false);
const [premiumHighlightsInjected, setPremiumHighlightsInjected] = useState(false);


// 🆕 New-post highlights pill
const [newSummary, setNewSummary] = useState(null);
const [newSummaryLoading, setNewSummaryLoading] = useState(false);
const [newHighlightsInjected, setNewHighlightsInjected] = useState(false);

// 🆕 Gate (decides which pill to show: badge OR new)
const [highlightsGate, setHighlightsGate] = useState(null);
const [highlightsGateLoading, setHighlightsGateLoading] = useState(false);


const [silentInsertedPill, setSilentInsertedPill] = useState(null);
const [silentInsertLoading, setSilentInsertLoading] = useState(false);

const [stakeRowsByPage, setStakeRowsByPage] = useState({}); // key: stakePage -> row

const [stakeDetailsOpen, setStakeDetailsOpen] = useState(false);
const [stakeDetailsPostId, setStakeDetailsPostId] = useState(null);
const [communityAvatarSelection, setCommunityAvatarSelection] = useState(null);

const [
  newsFeedArticles,
  setNewsFeedArticles,
] = useState([]);

const [
  newsFeedLoading,
  setNewsFeedLoading,
] = useState(false);

const [
  newsFeedError,
  setNewsFeedError,
] = useState('');

const [
  newsRotationKey,
  setNewsRotationKey,
] = useState('');


const newsFeedRequestedRef =
  useRef(false);


const openStakeDetails = (postId) => {
  setStakeDetailsPostId(postId);
  setStakeDetailsOpen(true);
};


  const feedRef = useRef();
  const headerRef = useRef();

const lastScrollTopRef = useRef(0);     // ✅ NEW: track last scroll position
const rafLockRef = useRef(false);       // ✅ NEW: rAF throttle (smooth)

const postOverlayScrollTopRef = useRef(0);
const postOverlayRestoreFrameRef = useRef(null);


// pull-to-refresh refs

const pullStartYRef = useRef(0);
const pullActiveRef = useRef(false);
const allowPullRef = useRef(false);
const restoreIosScrollRef = useRef(null);

const activePillLockRef = useRef(null);
const smartPillInsertRef = useRef(false);

  const canSeePlans = true;



  // 🆕 discovery row ref (horizontal scroller)
  const discoveryRowRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

const homeIsActive = isActive && location.pathname === '/home';

const communityAvatarViewerOpen = Boolean(
  homeIsActive &&
  communityAvatarSelection &&
  communityAvatarSelection.viewerKey ===
    String(user?._id || user?.username || '')
);

// Clear the viewer when leaving Home or switching accounts.
useEffect(() => {
  setCommunityAvatarSelection(null);
}, [homeIsActive, user?._id, user?.username]);


const [publishedPostToast, setPublishedPostToast] = useState(null);
const consumedPublishLocationRef = useRef(null);

useEffect(() => {
  if (!homeIsActive) {
    setPublishedPostToast(null);
    return;
  }

  const publishedPostId = location.state?.publishedPostId;
  const publishedBy = location.state?.publishedBy;

  if (
    !publishedPostId ||
    publishedBy !== user?.username ||
    consumedPublishLocationRef.current === location.key
  ) {
    return;
  }

  consumedPublishLocationRef.current = location.key;

  setPublishedPostToast({
    postId: publishedPostId,
    username: publishedBy,
  });

  // Consume the confirmation so Back won't show it again.
  const nextState = { ...location.state };
  delete nextState.publishedPostId;
  delete nextState.publishedBy;

  navigate(
    {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
    },
    {
      replace: true,
      state: nextState,
    }
  );
}, [
  homeIsActive,
  location.key,
  location.state,
  location.pathname,
  location.search,
  location.hash,
  user?.username,
  navigate,
]);

useEffect(() => {
  if (!publishedPostToast) return;

  const timer = window.setTimeout(() => {
    setPublishedPostToast(null);
  }, 8000);

  return () => window.clearTimeout(timer);
}, [publishedPostToast]);

const homeActiveRef = useRef(homeIsActive);



const homeActivityVersionRef = useRef(0);

useLayoutEffect(() => {
  homeActiveRef.current = homeIsActive;
  homeActivityVersionRef.current += 1;

  if (!homeIsActive) {
    // Close temporary UI while preserving feed data.
    setShowForm(false);
    setShowDrawer(false);
    setFeedFilterOpen(false);
    setSelfStoryDrawerOpen(false);
    setAccountsDrawerOpen(false);
    setStakeDetailsOpen(false);
    setShowPioneerExplorer(false);

    setPullDistance(0);
    setPullReady(false);

    allowPullRef.current = false;
    pullActiveRef.current = false;
    rafLockRef.current = false;

    // A cancelled highlights request must not leave loading flags set.
    setHighlightsGateLoading(false);
    setBadgeSummaryLoading(false);
    setNewSummaryLoading(false);
    setPremiumSummaryLoading(false);
    setRisingNowLoading(false);

    if (restoreIosScrollRef.current) {
      clearTimeout(restoreIosScrollRef.current);
      restoreIosScrollRef.current = null;
    }

    const feed = feedRef.current;

    if (feed) {
      feed.querySelectorAll('video, audio').forEach((media) => {
        media.pause();
      });

      feed.style.overflowY = 'auto';
      feed.style.webkitOverflowScrolling = 'touch';
    }
  }

  return () => {
    homeActiveRef.current = false;
    homeActivityVersionRef.current += 1;
  };
}, [homeIsActive]);


  const { getPostInteractions } = usePostContext();
  const { t } = useI18n();

// Map tab label → internal feedMode
// NOTE: "P2P" is just a shorter UI label for the old
// "Verified Payments" tab (posts with a transactionId).
const labelToMode = (label) => ({
  All: 'all',
  Following: 'following',
  Merchants: 'merchants',
  P2P: 'verified',
}[label] || 'all');



const NEW_LISTINGS_INSERT_INDEX = 3; // after the 4th post
const COMMUNITY_AVATARS_INSERT_INDEX = 4; // after the 5th post
const SUGGESTED_PIONEERS_INSERT_INDEX = 7; // after the 8th post
const SUGGESTED_INSERT_INDEX = 24; // after the 25th post


const handleOpenCommunityAvatar = (creation) => {
  if (!creation?._id) return;

  setCommunityAvatarSelection({
    creationId: String(creation._id),
    viewerKey: String(user?._id || user?.username || ''),
  });
};

const handleCloseCommunityAvatar = () => {
  setCommunityAvatarSelection(null);
};

const handleCreateCommunityAvatar = () => {
  setCommunityAvatarSelection(null);

  const y = feedRef.current?.scrollTop || 0;

  try {
    sessionStorage.setItem('bf_home_scrollTop', String(y));
  } catch {}

  navigate(
    canAccessStudio(user) ? '/studio/avatar' : '/edit-profile',
    {
      state: {
        from: 'home',
        restoreHomeScroll: true,
      },
    }
  );
};

const handleViewAllCommunityAvatars = () => {
  const y = feedRef.current?.scrollTop || 0;

  try {
    sessionStorage.setItem('bf_home_scrollTop', String(y));
  } catch {}

  navigate('/community-avatars', {
    state: {
      from: 'home',
      restoreHomeScroll: true,
    },
  });
};


const onOpenSuggestedProduct = (p) => {
  const y = feedRef.current?.scrollTop || 0;
  sessionStorage.setItem('bf_home_scrollTop', String(y));
  navigate(`/product/${p._id}`, { state: { from: 'home' } });
};

const onOpenNewListingProduct = (p) => {
  const y = feedRef.current?.scrollTop || 0;
  sessionStorage.setItem('bf_home_scrollTop', String(y));
  navigate(`/product/${p._id}`, { state: { from: 'home' } });
};


const onOpenSuggestedPioneer = (username) => {
  if (!username) return;

  const y = feedRef.current?.scrollTop || 0;
  sessionStorage.setItem('bf_home_scrollTop', String(y));

  navigate(`/user/${username}`, { state: { from: 'home' } });
};


const onOpenFeedNewsArticle = (article) => {
  if (!article?._id) return;

  const y = feedRef.current?.scrollTop || 0;

  try {
    sessionStorage.setItem('bf_home_scrollTop', String(y));
    sessionStorage.setItem('bf_home_return_source', 'news');
    sessionStorage.setItem('bf_home_news_article_id', String(article._id));
  } catch {}

  navigate(`/news/${article._id}`, {
    state: {
      article,
      from: 'home',
    },
  });
};

const handleViewAllNewListings = () => {
  const y = feedRef.current?.scrollTop || 0;
  sessionStorage.setItem('bf_home_scrollTop', String(y));
  navigate('/marketplace/banner/recent');
};





const handleViewAllSuggestedPioneers = () => {
  const y = feedRef.current?.scrollTop || 0;

  sessionStorage.setItem('bf_home_scrollTop', String(y));
  sessionStorage.setItem('bf_home_from', 'suggested-pioneers-row');

  navigate('/discover-people', {
    state: {
      from: 'home',
      restoreHomeScroll: true,
    },
  });
};

const generateSeed = () => Math.random().toString(36).slice(2, 10);



const fetchNewsFeedCandidates =
  React.useCallback(
    async ({
      force = false,
    } = {}) => {
      const cacheKey =
        getHomeNewsCacheKey(user);

      if (
        !user?.username ||
        !cacheKey
      ) {
        return;
      }

      /*
       * Normal Home loading:
       *
       * Restore the existing rotation instead of asking the
       * backend for another shuffled response.
       */
      if (!force) {
        const cachedRotation =
          HOME_NEWS_SESSION_CACHE.get(
            cacheKey
          );

        if (
          cachedRotation &&
          Array.isArray(
            cachedRotation.articles
          )
        ) {
          setNewsFeedArticles(
            cachedRotation.articles
          );

          setNewsRotationKey(
            cachedRotation.rotationKey ||
            ''
          );

          setNewsFeedError('');

          newsFeedRequestedRef.current =
            cacheKey;

          return cachedRotation.articles;
        }

        /*
         * Prevent repeated requests during the same mounted
         * Home instance.
         */
        if (
          newsFeedRequestedRef.current ===
          cacheKey
        ) {
          return;
        }
      }

      try {
        /*
         * Store the identity key rather than only true/false.
         * This allows account switching to request a different
         * rotation for the newly selected identity.
         */
        newsFeedRequestedRef.current =
          cacheKey;

        setNewsFeedLoading(true);
        setNewsFeedError('');

        const response =
          await api.get(
            '/news/feed',
            {
              params: {
                limit:
                  MAX_NEWS_ITEMS,
              },
            }
          );

        const nextArticles =
          Array.isArray(
            response.data?.articles
          )
            ? response.data.articles
            : [];

        const nextRotationKey =
          response.data
            ?.rotationKey ||
          '';

        /*
         * Replace the cached rotation only after a successful
         * response.
         */
        HOME_NEWS_SESSION_CACHE.set(
          cacheKey,
          {
            articles:
              nextArticles,
            rotationKey:
              nextRotationKey,
          }
        );

        setNewsFeedArticles(
          nextArticles
        );

        setNewsRotationKey(
          nextRotationKey
        );

        return nextArticles;
      } catch (error) {
        console.error(
          '❌ Failed to load Home News discussions:',
          error
        );

        setNewsFeedError(
          error?.response?.data
            ?.message ||
          'News discussions could not be loaded.'
        );

        /*
         * Do not clear the current articles when a manual
         * refresh fails. Keeping the old rotation creates a
         * much more stable UI.
         */
        const cachedRotation =
          HOME_NEWS_SESSION_CACHE.get(
            cacheKey
          );

        if (
          cachedRotation &&
          Array.isArray(
            cachedRotation.articles
          )
        ) {
          setNewsFeedArticles(
            cachedRotation.articles
          );

          setNewsRotationKey(
            cachedRotation.rotationKey ||
            ''
          );
        } else {
          /*
           * Allow a future normal attempt when the initial
           * request failed and nothing has been cached.
           */
          newsFeedRequestedRef.current =
            '';
        }

        return null;
      } finally {
        setNewsFeedLoading(false);
      }
    },
    [
      user?._id,
      user?.username,
    ]
  );





useEffect(() => {
  if (!homeIsActive || !user?.username) return;
  if (activeFilter !== 'All') return;

  fetchNewsFeedCandidates();
}, [
  homeIsActive,
  user?.username,
  activeFilter,
  fetchNewsFeedCandidates,
]);


useEffect(() => {
  const timer = window.setTimeout(() => {
    setShowPioneerFabLabel(false);
  }, 5000);

  return () => {
    window.clearTimeout(timer);
  };
}, []);


const PULL_MAX = 120;       // max icon travel
const PULL_TRIGGER = 78;    // release threshold


const PILL_TRIGGER_SCROLL = 1000;
const PILL_CONSUME_TOP_SCROLL = 80;
const PILL_AUTO_INSERT_TOP_SCROLL = 650;

const lockFeedDuringPull = () => {
  const feed = feedRef.current;
  if (!feed) return;

  feed.style.overflowY = 'hidden';
  feed.style.webkitOverflowScrolling = 'auto';
};

const unlockFeedAfterPull = () => {
  const feed = feedRef.current;
  if (!feed) return;

  feed.style.overflowY = 'auto';
  feed.style.webkitOverflowScrolling = 'touch';
};


const resetPull = () => {
  pullActiveRef.current = false;
  allowPullRef.current = false;
  setPullDistance(0);
  setPullReady(false);
  unlockFeedAfterPull();
};


const forceFeedSnapBack = () => {
  const feed = feedRef.current;
  if (!feed) return;

  if (restoreIosScrollRef.current) {
    clearTimeout(restoreIosScrollRef.current);
    restoreIosScrollRef.current = null;
  }

  // iOS trick: briefly disable momentum scrolling,
  // force top, then restore normal scrolling
  feed.style.webkitOverflowScrolling = 'auto';
  feed.style.overflowY = 'hidden';

  feed.scrollTop = 0;
  feed.scrollTo({ top: 0, behavior: 'auto' });

  requestAnimationFrame(() => {
    const currentFeed = feedRef.current;
    if (!currentFeed) return;

    currentFeed.scrollTop = 0;
    currentFeed.scrollTo({ top: 0, behavior: 'auto' });

    restoreIosScrollRef.current = setTimeout(() => {
      const finalFeed = feedRef.current;
      if (!finalFeed) return;

      finalFeed.style.overflowY = 'auto';
      finalFeed.style.webkitOverflowScrolling = 'touch';
      finalFeed.scrollTop = 0;
      finalFeed.scrollTo({ top: 0, behavior: 'auto' });

      restoreIosScrollRef.current = null;
    }, 40);
  });
};




const isTouchInsideDiscovery = (target) => {
  const row = discoveryRowRef.current;
  if (!row) return false;
  return row.contains(target);
};



const handlePullStart = (e) => {
  if (reloading || pullRefreshing || loading) return;

  const feed = feedRef.current;
  if (!feed) return;

  // 🚫 NEW: block pull if touch starts inside discovery row
  if (isTouchInsideDiscovery(e.target)) {
    allowPullRef.current = false;
    return;
  }

  // Only allow pull if at top
  if (feed.scrollTop > 0) {
    allowPullRef.current = false;
    return;
  }

  const touch = e.touches?.[0];
  if (!touch) return;

  pullStartYRef.current = touch.clientY;
  allowPullRef.current = true;
  pullActiveRef.current = false;
};



const handlePullMove = (e) => {
  if (!allowPullRef.current || reloading || pullRefreshing || loading) return;

  const feed = feedRef.current;
  if (!feed) return;

  const touch = e.touches?.[0];
  if (!touch) return;

  const deltaY = touch.clientY - pullStartYRef.current;

  // 🔥 NEW: ignore horizontal gestures
  const deltaX = Math.abs(e.touches[0].clientX - (e.touches[0].startX || 0));

  if (Math.abs(deltaX) > Math.abs(deltaY)) {
    return; // horizontal scroll → ignore pull
  }

  const rawDelta = deltaY;

  if (rawDelta > 0 || pullActiveRef.current || pullDistance > 0) {
    e.preventDefault();
    lockFeedDuringPull();
  }

  const next = Math.max(0, Math.min(PULL_MAX, rawDelta * 0.55));

  if (rawDelta > 0 || pullDistance > 0) {
    pullActiveRef.current = true;
  }

  setPullDistance(next);
  setPullReady(next >= PULL_TRIGGER);
};



const handlePullEnd = async () => {
  if (!pullActiveRef.current) {
    allowPullRef.current = false;
    unlockFeedAfterPull();
    return;
  }

  const shouldRefresh = pullDistance >= PULL_TRIGGER;

  pullActiveRef.current = false;
  allowPullRef.current = false;

  if (!shouldRefresh) {
    setPullDistance(0);
    setPullReady(false);
    unlockFeedAfterPull();
    return;
  }

  setPullRefreshing(true);
  setPullDistance(PULL_TRIGGER);

  try {
    await scrollToTop({ forceNewSeed: true });
  } catch (err) {
    console.error('❌ Pull reload failed:', err);
  } finally {
    setPullRefreshing(false);
    setPullDistance(0);
    setPullReady(false);
    unlockFeedAfterPull();
    forceFeedSnapBack();
  }
};



useEffect(() => {
  return () => {
    if (restoreIosScrollRef.current) {
      clearTimeout(restoreIosScrollRef.current);
    }
  };
}, []);








// --- badges helpers (works whether server gives badgesBrief or full badges) ---
const getBadgesBrief = (post) => {
  const brief = post?.user?.badgesBrief;
  if (Array.isArray(brief) && brief.length) return brief;

  // fallback: compress active badges -> ["BARTER_KING", ...]
  const full = post?.user?.badges;
  if (!Array.isArray(full)) return [];
  const now = Date.now();
  const active = full
    .filter(b => b?.type && (!b.expiresAt || new Date(b.expiresAt).getTime() > now))
    .map(b => String(b.type).toUpperCase());
  // dedupe, keep order by priority
  const seen = new Set();
  const PRIORITY = ["BARTER_KING","MERCHANT_STAR","CREATOR_FLAME","PI_GUARDIAN"];
  return [...active.filter(t => !seen.has(t) && (seen.add(t), true))]
    .sort((a,b) => PRIORITY.indexOf(a) - PRIORITY.indexOf(b));
};





const PREMIUM_PILL_COOLDOWN_MS =
  Math.max(1, parseInt(process.env.REACT_APP_FEED_PREMIUM_PILL_COOLDOWN_MINUTES || '5', 10)) * 60 * 1000;

useEffect(() => {
  if (!premiumHighlightsInjected) return;
  const t = setTimeout(() => setPremiumHighlightsInjected(false), PREMIUM_PILL_COOLDOWN_MS);
  return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [premiumHighlightsInjected]);



// scrollToTop now also sets a new feed seed so backend reshuffles
const scrollToTop = async ({ forceNewSeed = true } = {}) => {
  setReloading(true);

  try {
    if (feedRef.current) {
      feedRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }

    if (forceNewSeed) {
      const seed = generateSeed();
      sessionStorage.setItem('bf_feed_seed', seed);
      sessionStorage.setItem('bf_feed_seed_ts', String(Date.now()));
    }

    // 🚀 keep current UI/feed visible while loader spins

await loadInitialPosts(
  user.username,
  true,
  labelToMode(activeFilter)
);

if (activeFilter === 'All') {
  await fetchNewsFeedCandidates({
    force: true,
  });
}

if (refetchDiscovery) {
  await refetchDiscovery();
}

// ✅ clear Rising card only AFTER refresh is done
setRisingHighlightsInjected(false);
setRisingNowCard(null);
setRisingNowSummary(null);

// ✅ Remove Rising Now feed item from the posts array too
setPosts((prev) =>
  Array.isArray(prev)
    ? prev.filter((item) => item?.type !== 'rising_now_card')
    : prev
);

try {
  sessionStorage.removeItem(getRisingNowCardCacheKey(user.username));
} catch {}


  } catch (err) {
    console.error('❌ Error refreshing feed:', err);
  } finally {
    setReloading(false);
  }
};


  const fetchUserFollowStats = async () => {
    try {
      const res = await api.get(`/users/follow-data/${user.username}`);
      setUserStats(res.data);
    } catch (err) {
      console.error('❌ Failed to fetch follow stats:', err);
    }
  };




const persistAuthFromResponse = (res) => {
  const nextUser = res?.data?.user || res?.data?.currentUser || null;
  const nextToken = res?.data?.token || null;

  if (nextToken) {

    localStorage.setItem('token', nextToken);
    localStorage.setItem('barterfi_token', nextToken);
  }

  if (nextUser && typeof onUserUpdate === 'function') {
    onUserUpdate(nextUser);
  }

  return nextUser;
};

const fetchMyAppsForSwitcher = async () => {
  try {
    const res = await api.get('/apps/me');
    const apps = Array.isArray(res.data?.appProfiles) ? res.data.appProfiles : [];

    console.log('🟦 [Home] /apps/me result', {
      currentUser: user?.username,
      accountType: user?.accountType,
      appsCount: apps.length,
      apps,
    });

    setMyApps((prev) => {
      // If backend returns a valid non-empty list, trust it
      if (apps.length > 0) return apps;

      // If backend returns empty while on an app identity,
      // keep the previous list so the accounts icon doesn't disappear
      if (user?.accountType === 'app' && prev.length > 0) {
        return prev;
      }

      return apps;
    });
  } catch (err) {
    console.error('❌ Failed to fetch apps for switcher:', err);
    // keep previous myApps instead of wiping them out
  }
};



const handleSwitchIdentity = async (target = switchTarget) => {
  if (switchingIdentity || !target) return;

  try {
    setSwitchingIdentity(true);

    if (target.type === 'main') {
      const res = await api.post('/auth/switch-back');
      persistAuthFromResponse(res);
      await fetchMyAppsForSwitcher();
      pushRecentIdentity(target.id);

      setAccountsDrawerOpen(false);
      setShowDrawer(false);

      setTimeout(() => {
        fetchMyAppsForSwitcher();
      }, 150);

      return;
    }

    if (!target.targetUserId) {
      alert('This account is not ready for switching yet.');
      return;
    }

    const res = await api.post('/auth/switch-account', {
      targetUserId: target.targetUserId,
    });

    persistAuthFromResponse(res);
    await fetchMyAppsForSwitcher();
    pushRecentIdentity(target.id);

    setAccountsDrawerOpen(false);
    setShowDrawer(false);

    setTimeout(() => {
      fetchMyAppsForSwitcher();
    }, 150);
  } catch (err) {
    console.error('❌ Failed to switch identity:', err);
    alert(err?.response?.data?.message || 'Failed to switch identity.');
  } finally {
    setSwitchingIdentity(false);
  }
};


useEffect(() => {
  if (!homeIsActive || !user?.username) return;

  fetchMyAppsForSwitcher();
}, [
  homeIsActive,
  user?.username,
  user?.accountType,
  user?._id,
  user?.ownerUserId,
]);


const activeApps = myApps.filter(
  (app) =>
    String(app?.status || '').toLowerCase() === 'active' &&
    app?.linkedUser
);

// Main account identity
const mainIdentity = {
  id: `main-${user?.ownerUserId || user?._id || user?.username || 'me'}`,
  type: 'main',
  name:
    user?.accountType === 'app'
      ? user?.ownerName || 'Main Account'
      : user?.name || user?.username || 'Main Account',
  username:
    user?.accountType === 'app'
      ? user?.ownerUsername || 'owner'
      : user?.username || 'owner',
  profilePic:
    user?.accountType === 'app'
      ? user?.ownerProfilePic || placeholderProfile
      : user?.profilePic || placeholderProfile,
  targetUserId:
    user?.accountType === 'app'
      ? user?.ownerUserId || null
      : user?._id || null,
};

// App identities
const appIdentities = activeApps.map((app) => {
  const linkedUser =
    app?.linkedUser && typeof app.linkedUser === 'object'
      ? app.linkedUser
      : null;

  const linkedUserId = String(linkedUser?._id || app?.linkedUser || '');
  const currentUserId = String(user?._id || '');

  const isCurrentAppIdentity =
    user?.accountType === 'app' &&
    currentUserId &&
    linkedUserId &&
    currentUserId === linkedUserId;

  const liveProfilePic =
    (isCurrentAppIdentity ? user?.profilePic : null) ||
    linkedUser?.profilePic ||
    '';

  return {
    id: `app-${app?._id}`,
    type: 'app',
    appId: app?._id,
    name: app?.name || app?.username || linkedUser?.name || 'App Account',
    username: app?.username || linkedUser?.username || '',
    profilePic:
      liveProfilePic ||
      app?.effectiveProfilePic ||
      app?.profilePic ||
      app?.logo ||
      placeholderProfile,
    targetUserId: linkedUser?._id || app?.linkedUser || null,
  };
});

// Current identity
const currentIdentity =
  user?.accountType === 'app'
    ? appIdentities.find((acc) => acc.targetUserId === user?._id) || {
        id: `app-current-${user?._id || user?.username || 'unknown'}`,
        type: 'app',
        name: user?.name || user?.username || 'App Account',
        username: user?.username || '',
        profilePic: user?.profilePic || placeholderProfile,
        targetUserId: user?._id || null,
      }
    : mainIdentity;

// Full identity pool = main + every active app
const identityPool = [mainIdentity, ...appIdentities];

// Everything except current
const otherAccounts = identityPool.filter(
  (acc) => acc.id !== currentIdentity?.id
);


// Recent accounts memory
const ACCOUNT_HISTORY_KEY = 'bf_identity_switch_history';

const getStoredAccountHistory = () => {
  try {
    const raw = localStorage.getItem(ACCOUNT_HISTORY_KEY);
    const parsed = JSON.parse(raw || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const saveStoredAccountHistory = (ids) => {
  try {
    localStorage.setItem(ACCOUNT_HISTORY_KEY, JSON.stringify(ids.slice(0, 10)));
  } catch {}
};

// Order other accounts by recent usage
const recentHistoryIds = getStoredAccountHistory();

const orderedOtherAccounts = [...otherAccounts].sort((a, b) => {
  const ai = recentHistoryIds.indexOf(a.id);
  const bi = recentHistoryIds.indexOf(b.id);

  if (ai === -1 && bi === -1) return 0;
  if (ai === -1) return 1;
  if (bi === -1) return -1;
  return ai - bi;
});

const switchTarget = orderedOtherAccounts[0] || null;

// Show accounts icon whenever total identities are 3 or more
const shouldShowAccountsIcon = identityPool.length >= 3;

console.log('🟨 [Home] account switch debug', {
  currentUser: user?.username,
  accountType: user?.accountType,
  myAppsCount: myApps.length,
  activeAppsCount: activeApps.length,
  identityPool,
  orderedOtherAccounts,
  shouldShowAccountsIcon,
});



const insertBadgeHighlights = async ({ scrollToTop = false } = {}) => {
  if (!homeActiveRef.current) return;

  const activityVersion = homeActivityVersionRef.current;

  if (!user?.username || badgeHighlightsInjected) {
    if (scrollToTop && feedRef.current) {

feedRef.current.scrollTo({ top: 0, behavior: 'smooth' });

    }
    return;
  }

  try {


const res = await api.get(
  `/feed/badge-highlights/list/${user.username}`,
  { params: { limit: 40 } }
);

if (
  !homeActiveRef.current ||
  activityVersion !== homeActivityVersionRef.current
) {
  return;
}

const items = Array.isArray(res.data?.items) ? res.data.items : [];

    if (!items.length) {
      setBadgeHighlightsInjected(true);
      setSilentInsertedPill('badge');
      return;
    }

    setPosts((prev) => {
      const prevArr = Array.isArray(prev) ? prev : [];
      const existingIds = new Set(prevArr.map((p) => String(p._id)));

      const newOnes = items.filter(
        (p) => p && p._id && !existingIds.has(String(p._id))
      );

      return [...newOnes, ...prevArr];
    });

    setBadgeHighlightsInjected(true);
    setSilentInsertedPill('badge');

    if (scrollToTop && feedRef.current) {

feedRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  } catch (err) {
    console.error('❌ Failed to load badge highlights list:', err);
  }
};



const handleBadgePillClick = () => {
  if (silentInsertLoading || smartPillInsertRef.current) return;
  insertBadgeHighlights({ scrollToTop: true });
};


const pushRecentIdentity = (identityId) => {
  if (!identityId) return;
  const existing = getStoredAccountHistory().filter((id) => id !== identityId);
  saveStoredAccountHistory([identityId, ...existing]);
};




const insertNewHighlights = async ({ scrollToTop = false } = {}) => {
  if (!homeActiveRef.current) return;

  const activityVersion = homeActivityVersionRef.current;

  if (!user?.username || newHighlightsInjected) {

    if (scrollToTop && feedRef.current) {

feedRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
    return;
  }

  try {

const res = await api.get(
  `/feed/new-highlights/list/${user.username}`,
  { params: { limit: 40 } }
);

if (
  !homeActiveRef.current ||
  activityVersion !== homeActivityVersionRef.current
) {
  return;
}

const items = Array.isArray(res.data?.items) ? res.data.items : [];

    if (!items.length) {
      setNewHighlightsInjected(true);
      setSilentInsertedPill('new');
      return;
    }

    setPosts((prev) => {
      const prevArr = Array.isArray(prev) ? prev : [];
      const existingIds = new Set(prevArr.map((p) => String(p._id)));

      const newOnes = items.filter(
        (p) => p && p._id && !existingIds.has(String(p._id))
      );

      return [...newOnes, ...prevArr];
    });

    setNewHighlightsInjected(true);
    setSilentInsertedPill('new');

    if (scrollToTop && feedRef.current) {

feedRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  } catch (err) {
    console.error('❌ Failed to load new highlights list:', err);
  }
};


const handleNewPillClick = () => {
  if (silentInsertLoading || smartPillInsertRef.current) return;
  insertNewHighlights({ scrollToTop: true });
};


const buildRisingNowFeedItem = (card) => ({
  _id: `rising-now-card-${card?.version || Date.now()}`,
  type: 'rising_now_card',
  showCard: true,
  risingNowCard: card,
  createdAt: new Date().toISOString(),
});



const insertRisingNowHighlight = async ({ scrollToTop = false } = {}) => {
  if (!homeActiveRef.current) return;

  const activityVersion = homeActivityVersionRef.current;

  if (!user?.username || risingHighlightsInjected) {
    if (scrollToTop && feedRef.current) {
      feedRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
    return;
  }

  try {
    setRisingNowLoading(true);

const res = await api.get(
  `/feed/rising-now-highlights/list/${user.username}`
);

if (
  !homeActiveRef.current ||
  activityVersion !== homeActivityVersionRef.current
) {
  return;
}

const card = res.data || null;

    if (!card?.showCard) {
      setRisingHighlightsInjected(true);
      setRisingNowCard(null);
      setSilentInsertedPill('rising_now');
      return;
    }

    const risingFeedItem = buildRisingNowFeedItem(card);

    setPosts((prev) => {
      const prevArr = Array.isArray(prev) ? prev : [];

      // Remove old Rising Now card so we never duplicate it
      const withoutOldRising = prevArr.filter(
        (item) => item?.type !== 'rising_now_card'
      );

      return [risingFeedItem, ...withoutOldRising];
    });

    setRisingNowCard(card);
    setRisingHighlightsInjected(true);
    setSilentInsertedPill('rising_now');

    try {
      sessionStorage.setItem(
        getRisingNowCardCacheKey(user.username),
        JSON.stringify(card)
      );
    } catch {}

    if (scrollToTop && feedRef.current) {
      feedRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  } catch (err) {
    console.error('❌ Failed to load Rising Now card:', err);

} finally {
  if (
    homeActiveRef.current &&
    activityVersion === homeActivityVersionRef.current
  ) {
    setRisingNowLoading(false);
  }
}
};


const handleRisingNowPillClick = () => {
  if (silentInsertLoading || smartPillInsertRef.current) return;
  insertRisingNowHighlight({ scrollToTop: true });
};

useEffect(() => {
  const cached = getInitialRisingNowCard(user?.username);

  setRisingNowCard(cached);
  setRisingHighlightsInjected(!!cached);
}, [user?.username]);





const insertPremiumHighlights = async ({ scrollToTop = false } = {}) => {
  if (!homeActiveRef.current) return;

  const activityVersion = homeActivityVersionRef.current;

  if (!user?.username || premiumHighlightsInjected) {

    if (scrollToTop && feedRef.current) {

feedRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
    return;
  }

  try {

const res = await api.get(
  `/feed/premium-plus-highlights/list/${user.username}`
);

if (
  !homeActiveRef.current ||
  activityVersion !== homeActivityVersionRef.current
) {
  return;
}

const items = Array.isArray(res.data?.items) ? res.data.items : [];

    if (!items.length) {
      setPremiumHighlightsInjected(true);
      setSilentInsertedPill('premium_plus');
      return;
    }

    setPosts((prev) => {
      const prevArr = Array.isArray(prev) ? prev : [];
      const existingIds = new Set(prevArr.map((p) => String(p._id)));

      const newOnes = items.filter(
        (p) => p && p._id && !existingIds.has(String(p._id))
      );

      return [...newOnes, ...prevArr];
    });

    setPremiumHighlightsInjected(true);
    setSilentInsertedPill('premium_plus');

    if (scrollToTop && feedRef.current) {

feedRef.current.scrollTo({ top: 0, behavior: 'smooth' });

    }
  } catch (err) {
    console.error('❌ Failed to load premium+ highlights list:', err);
  }
};


const handlePremiumPillClick = () => {
  if (silentInsertLoading || smartPillInsertRef.current) return;
  insertPremiumHighlights({ scrollToTop: true });
};

const gatePill = highlightsGate?.pill;


const autoInsertPendingPillNearTop = React.useCallback(async () => {
  if (!homeIsActive || !homeActiveRef.current) return;
  if (!gatePill) return;
  if (activeFilter !== 'All') return;
  if (restoringHomeScroll) return;
  if (reloading || pullRefreshing) return;
  if (smartPillInsertRef.current) return;
  if (silentInsertLoading) return;
  if (silentInsertedPill === gatePill) return;

  try {
    smartPillInsertRef.current = true;
    setSilentInsertLoading(true);

    if (gatePill === 'premium_plus') {
      await insertPremiumHighlights({ scrollToTop: false });
    } else if (gatePill === 'badge') {
      await insertBadgeHighlights({ scrollToTop: false });
    } else if (gatePill === 'new') {
      await insertNewHighlights({ scrollToTop: false });
    } else if (gatePill === 'rising_now') {
      await insertRisingNowHighlight({ scrollToTop: false });
    }
  } finally {
    setSilentInsertLoading(false);
    smartPillInsertRef.current = false;
  }
}, [
  homeIsActive,
  gatePill,
  activeFilter,
  restoringHomeScroll,
  reloading,
  pullRefreshing,
  silentInsertLoading,
  silentInsertedPill,
  premiumHighlightsInjected,
  badgeHighlightsInjected,
  newHighlightsInjected,
  risingHighlightsInjected,
]);

const handlePollVoteUpdate = React.useCallback((postId, updatedPoll) => {
  if (!postId || !updatedPoll) return;

  setPosts((prev) => {
    const arr = Array.isArray(prev) ? prev : [];

    return arr.map((post) => {
      if (String(post._id) !== String(postId)) return post;

      return {
        ...post,
        poll: updatedPoll,
      };
    });
  });
}, [setPosts]);


const fetchStakeRow = async (stakePage) => {
  try {
    const res = await api.get('/feed/stake-row', {
      params: { limit: 10, page: stakePage },
      headers: { 'Cache-Control': 'no-cache' }, // optional extra safety
    });

    const items = Array.isArray(res.data?.items) ? res.data.items : [];

    setStakeRowsByPage(prev => ({
      ...prev,
      [stakePage]: {
        type: 'staked_row',
        title: res.data?.title || 'Staked Posts',
        items,
      }
    }));
  } catch (e) {
    console.error('❌ Failed to fetch stake row:', e);
    setStakeRowsByPage(prev => ({ ...prev, [stakePage]: null }));
  }
};



const openPostDetails = (postId, extraState = {}) => {
  if (!postId) return;

  const y = feedRef.current?.scrollTop || 0;
  sessionStorage.setItem('bf_home_scrollTop', String(y));
  sessionStorage.setItem('bf_home_fromPostId', String(postId));

  navigate(`/post/${postId}`, {
    state: {
      ...extraState,
      from: 'home',
    },
  });
};



const handleNewPost = async (newPost) => {
  if (!newPost) return setShowForm(false);
  try {
    const res = await api.get(`/users/${user.username}`);
    const freshUser = res.data;

    const now = new Date();
    const paidUntilOk =
      freshUser?.merchantPaidUntil && new Date(freshUser.merchantPaidUntil) > now;
    const explicitlyPaid = freshUser?.paymentStatus === 'paid';
    const onTrial = freshUser?.paymentStatus === 'trial' && paidUntilOk;

    const isPaidActive = Boolean(explicitlyPaid || onTrial || paidUntilOk);

    const enrichedPost = {
      ...newPost,
      userType: isPaidActive ? 'merchant' : 'pioneer', // UI role
      isMerchant: isPaidActive,                         // badge gate
      user: {
        name: freshUser.name,
        username: freshUser.username,
        profilePic: freshUser.profilePic,
        userType: freshUser.userType,
        isMerchant: isPaidActive,                      // mirror for consistency
        merchantPaidUntil: freshUser.merchantPaidUntil,
        paymentStatus: freshUser.paymentStatus,
        merchantActive: freshUser.merchantActive,
      }
    };

    addPost(enrichedPost);
    setShowForm(false);
    setTimeout(() => scrollToTop(), 1000);
  } catch (err) {
    console.error('❌ Failed to fetch fresh user info for new post:', err);
    setShowForm(false);
  }
};


useEffect(() => {

if (!homeIsActive || !user?.username) return;
  let cancelled = false;
  let intervalId = null;

  const fetchGateAndSummary = async () => {

if (
  cancelled ||
  !homeActiveRef.current ||
  activePillLockRef.current
) {
  return;
}
    setHighlightsGateLoading(true);

 
    try {

// Gate decides which pill is allowed to show: premium_plus -> badge -> new -> rising_now  
    const gateRes = await api.get(`/feed/highlights-gate/summary/${user.username}`);
      const gate = gateRes.data || null;

      if (cancelled) return;
      setHighlightsGate(gate);

      const pill = gate?.pill;


      // ✅ Clear stale summaries so only ONE pill can render cleanly
      if (pill !== 'badge') setBadgeSummary(null);
      if (pill !== 'new') setNewSummary(null);
      if (pill !== 'premium_plus') setPremiumSummary(null);
      if (pill !== 'rising_now') setRisingNowSummary(null);

      // ✅ Fetch ONLY what we need
      if (pill === 'premium_plus') {
        // gate already includes count + previewUsers
        if (!cancelled) {
          setPremiumSummary({
            hasHighlights: true,
            count: gate?.count || 0,
            latestPostAt: gate?.latestPostAt || null,
            previewUsers: Array.isArray(gate?.previewUsers) ? gate.previewUsers : [],
          });
        }
        return;
      }

      if (pill === 'badge') {
        setBadgeSummaryLoading(true);
        const res = await api.get(`/feed/badge-highlights/summary/${user.username}`);
        if (!cancelled) setBadgeSummary(res.data || null);
        return;
      }

      if (pill === 'new') {
        setNewSummaryLoading(true);
        const res = await api.get(`/feed/new-highlights/summary/${user.username}`);
        if (!cancelled) setNewSummary(res.data || null);
        return;
      }

      if (pill === 'rising_now') {
        setRisingNowLoading(true);
        const res = await api.get(`/feed/rising-now-highlights/summary/${user.username}`);
        if (!cancelled) setRisingNowSummary(res.data || null);
        return;
      }

      // pill is null -> ensure all summaries cleared

      if (!cancelled) {
        setBadgeSummary(null);
        setNewSummary(null);
        setPremiumSummary(null);
        setRisingNowSummary(null);
      }

    } catch (err) {
      console.error('❌ Failed to fetch highlights gate/summary:', err);
      if (!cancelled) {

        setHighlightsGate(null);
        setBadgeSummary(null);
        setNewSummary(null);
        setPremiumSummary(null);
        setRisingNowSummary(null);
      }
    } finally {
      if (!cancelled) {

        setHighlightsGateLoading(false);
        setBadgeSummaryLoading(false);
        setNewSummaryLoading(false);
        setPremiumSummaryLoading(false);
        setRisingNowLoading(false);
      }
    }
  };

  fetchGateAndSummary();

intervalId = setInterval(() => {
  if (!cancelled && homeActiveRef.current) {
    fetchGateAndSummary();
  }
}, 20000);

  return () => {
    cancelled = true;
    if (intervalId) clearInterval(intervalId);
  };

}, [homeIsActive, user?.username]);

const canShowAnyPill =
  homeIsActive &&
  showPillGate &&
  !highlightsGateLoading &&
  !badgeSummaryLoading &&
  !newSummaryLoading &&
  !premiumSummaryLoading;

const showPremiumHighlightsPill =
  gatePill === 'premium_plus' &&
  !!premiumSummary?.hasHighlights &&
  silentInsertedPill !== 'premium_plus' &&
  canShowAnyPill;

const showBadgeHighlightsPill =
  gatePill === 'badge' &&
  !!badgeSummary?.hasHighlights &&
  silentInsertedPill !== 'badge' &&
  canShowAnyPill;

const showNewHighlightsPill =
  gatePill === 'new' &&
  !!newSummary?.hasHighlights &&
  silentInsertedPill !== 'new' &&
  canShowAnyPill;

const showRisingNowHighlightsPill =
  gatePill === 'rising_now' &&
  !!risingNowSummary?.hasHighlights &&
  silentInsertedPill !== 'rising_now' &&
  canShowAnyPill;

  
useEffect(() => {
  activePillLockRef.current = silentInsertedPill;
}, [silentInsertedPill]);


useEffect(() => {
  console.log('🧩 [Home] selfStoryDrawerOpen:', selfStoryDrawerOpen);
}, [selfStoryDrawerOpen]);

// Home.js
  const handleDeletePost = (postId) => {
    setPosts(prev => prev.filter(p => p._id !== postId));
    try {
      const raw = localStorage.getItem('profilePosts');
      if (raw) {
        const next = JSON.parse(raw).filter(p => p._id !== postId);
        localStorage.setItem('profilePosts', JSON.stringify(next));
      }
    } catch {}
    window.dispatchEvent(new CustomEvent('post:deleted', { detail: { postId } }));
  };






useEffect(() => {
  if (!homeIsActive || !user?.username) return;

  try {
    const raw = localStorage.getItem(HOME_FEED_CACHE_KEY);

    if (raw && posts.length === 0) {
      const parsed = JSON.parse(raw);

      const cachedPosts = Array.isArray(parsed?.data)
        ? parsed.data.map((post) => {
            if (!post || post.type === 'sponsored') {
              return post;
            }

            return {
              ...post,
              viewCount: Math.max(0, Number(post.viewCount) || 0),
            };
          })
        : [];

      const cachedTs = Number(parsed?.ts || 0);

      if (cachedPosts.length > 0) {
        console.log('⚡ Hydrating feed from persistent cache', {
          ageMs: cachedTs ? Date.now() - cachedTs : null,
          count: cachedPosts.length,
        });

        setPosts(cachedPosts);
      }
    }
  } catch (err) {
    console.error('❌ Failed to hydrate cache:', err);
  }

  if (!hasLoadedPosts && !loading) {
    loadInitialPosts(
      user.username,
      false,
      labelToMode(activeFilter)
    );
  }
}, [
  homeIsActive,
  user?.username,
  hasLoadedPosts,
  loading,
  activeFilter,
]);




useEffect(() => {
  if (!Array.isArray(posts) || posts.length === 0) return;

  try {
    const trimmed = posts.slice(0, 12); // keep it light

    localStorage.setItem(
      HOME_FEED_CACHE_KEY,
      JSON.stringify({
        data: trimmed,
        ts: Date.now(),
      })
    );
  } catch (err) {
    console.error('❌ Failed to cache posts:', err);
  }
}, [posts]);



useEffect(() => {
  if (!homeIsActive || !user?.username) return;

  fetchUserFollowStats();
  refreshUnreadCount(user.username);

  const interval = setInterval(() => {
    if (homeActiveRef.current) {
      refreshUnreadCount(user.username);
    }
  }, 10000);

  return () => clearInterval(interval);
}, [homeIsActive, user?.username, refreshUnreadCount]);


  // Keep: some other popups use .popup-backdrop; our Home drawer is namespaced and won’t trigger this.


useEffect(() => {
  if (!homeIsActive) return;

  const updatePopupScrollLock = () => {
    if (!homeActiveRef.current) return;

    const hasPopupOpen =
      !!document.querySelector('.popup-backdrop');

    document.body.classList.toggle('no-scroll', hasPopupOpen);
  };

  updatePopupScrollLock();

  const observer = new MutationObserver(updatePopupScrollLock);

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });

  return () => {
    observer.disconnect();

    // Preserve a lock if another screen already has a popup open.
    const hasPopupOpen =
      !!document.querySelector('.popup-backdrop');

    document.body.classList.toggle('no-scroll', hasPopupOpen);
  };
}, [homeIsActive]);



useEffect(() => {
  if (!homeIsActive) return;

  const body = document.body;

  let wasPostOverlayOpen =
    body.classList.contains('bf-post-overlay-open');

  const handlePostOverlayState = () => {
    if (!homeActiveRef.current) return;

    const feed = feedRef.current;
    if (!feed) return;

    const isPostOverlayOpen =
      body.classList.contains('bf-post-overlay-open');

    if (isPostOverlayOpen === wasPostOverlayOpen) return;

    if (isPostOverlayOpen) {
      if (postOverlayRestoreFrameRef.current) {
        cancelAnimationFrame(postOverlayRestoreFrameRef.current);
        postOverlayRestoreFrameRef.current = null;
      }

      postOverlayScrollTopRef.current = feed.scrollTop;
      lastScrollTopRef.current = feed.scrollTop;
    }

    if (!isPostOverlayOpen && wasPostOverlayOpen) {
      const savedTop = postOverlayScrollTopRef.current;

      postOverlayRestoreFrameRef.current = requestAnimationFrame(() => {
        postOverlayRestoreFrameRef.current = null;

        if (!homeActiveRef.current) return;

        const currentFeed = feedRef.current;
        if (!currentFeed) return;

        currentFeed.scrollTop = savedTop;
        currentFeed.scrollTo({
          top: savedTop,
          behavior: 'auto',
        });

        lastScrollTopRef.current = savedTop;
      });
    }

    wasPostOverlayOpen = isPostOverlayOpen;
  };

  const observer = new MutationObserver(handlePostOverlayState);

  observer.observe(body, {
    attributes: true,
    attributeFilter: ['class'],
  });

  handlePostOverlayState();

  return () => {
    observer.disconnect();

    if (postOverlayRestoreFrameRef.current) {
      cancelAnimationFrame(postOverlayRestoreFrameRef.current);
      postOverlayRestoreFrameRef.current = null;
    }
  };
}, [homeIsActive]);



useEffect(() => {
  if (!homeIsActive) return;

  const feed = feedRef.current;
  if (!feed) return;

  let scrollTimeout;
  let scrollFrame = null;
  let cancelled = false;

  // Avoid treating a preserved position as a new upward scroll.
  lastScrollTopRef.current = Math.max(0, feed.scrollTop);

  const handleScroll = () => {
    if (
      cancelled ||
      !homeActiveRef.current ||
      rafLockRef.current
    ) {
      return;
    }

    rafLockRef.current = true;

    scrollFrame = requestAnimationFrame(() => {
      scrollFrame = null;

      if (cancelled || !homeActiveRef.current) {
        rafLockRef.current = false;
        return;
      }

      const scrollTop = Math.max(0, feed.scrollTop);
      const clientHeight = feed.clientHeight;
      const scrollHeight = feed.scrollHeight;

      const nearBottom =
        scrollTop + clientHeight >= scrollHeight - 300;

      setShowScrollTop(scrollTop > 2000);
      setShowPillGate(scrollTop > PILL_TRIGGER_SCROLL);

      const hasReachedPillConsumeTop =
        scrollTop <= PILL_CONSUME_TOP_SCROLL;

      if (
        hasReachedPillConsumeTop &&
        silentInsertedPill &&
        !smartPillInsertRef.current &&
        !silentInsertLoading
      ) {
        setSilentInsertedPill(null);
        setHighlightsGate(null);

        setPremiumSummary(null);
        setBadgeSummary(null);
        setNewSummary(null);
        setRisingNowSummary(null);
      }

      const prevTop = lastScrollTopRef.current || 0;
      const delta = scrollTop - prevTop;

      const THRESHOLD = 10;
      const ALWAYS_SHOW_TOP = 80;

      const isScrollingUp = delta < -THRESHOLD;
      const isNearPillInsertTop =
        scrollTop <= PILL_AUTO_INSERT_TOP_SCROLL;

      if (
        isScrollingUp &&
        isNearPillInsertTop &&
        !restoringHomeScroll &&
        !reloading &&
        !pullRefreshing &&
        showPillGate &&
        gatePill &&
        silentInsertedPill !== gatePill &&
        !smartPillInsertRef.current
      ) {
        autoInsertPendingPillNearTop();
      }

      if (scrollTop < ALWAYS_SHOW_TOP) {
        setUiHidden(false);
      } else if (Math.abs(delta) > THRESHOLD) {
        setUiHidden(delta > 0);
      }

      lastScrollTopRef.current = scrollTop;

      clearTimeout(scrollTimeout);

      scrollTimeout = setTimeout(() => {
        if (
          !cancelled &&
          homeActiveRef.current &&
          nearBottom &&
          hasMore &&
          !loading
        ) {
          fetchMorePosts(user.username);
        }
      }, 150);

      rafLockRef.current = false;
    });
  };

  feed.addEventListener('scroll', handleScroll, { passive: true });
  handleScroll();

  return () => {
    cancelled = true;

    clearTimeout(scrollTimeout);

    if (scrollFrame !== null) {
      cancelAnimationFrame(scrollFrame);
    }

    rafLockRef.current = false;
    feed.removeEventListener('scroll', handleScroll);
  };
}, [
  homeIsActive,
  loading,
  hasMore,
  user.username,
  fetchMorePosts,
  silentInsertedPill,
  silentInsertLoading,
  showPillGate,
  gatePill,
  restoringHomeScroll,
  reloading,
  pullRefreshing,
  autoInsertPendingPillNearTop,
]);





  useEffect(() => {
    const feed = feedRef.current;
    if (feed) {
      const minHeight = window.innerHeight - 140;
      feed.style.minHeight = `${minHeight}px`;
    }
  }, []);




// --- helpers (tolerate top-level or nested shapes) ---
const getAuthorUsername = (post) =>
  post?.username || post?.user?.username || '';

const getUserType = (post) =>
  post?.userType || post?.user?.userType || 'pioneer';

const getIsMerchant = (post) =>
  post?.isMerchant === true || post?.user?.isMerchant === true || getUserType(post) === 'merchant';

// Paid OR active trial merchants ONLY (no badge for just approved/unpaid/expired)
const isPaidActiveMerchant = (post) => {
  const u = post?.user || {};

  // If backend precomputes this, trust it.
  if (u.merchantActive === true) return true;

  const now = new Date();
  const paidUntilOk = u.merchantPaidUntil && new Date(u.merchantPaidUntil) > now;
  const explicitlyPaid = u.paymentStatus === 'paid';
  const onTrial = u.paymentStatus === 'trial' && paidUntilOk;

  // Badge shows only when they've paid OR are on an active trial
  return Boolean(explicitlyPaid || onTrial || paidUntilOk);
};



// Normalize following list to usernames (handles strings or objects)
const followingUsernames = React.useMemo(() => {
  const arr = Array.isArray(userStats?.following) ? userStats.following : [];
  if (arr.length === 0) return [];
  if (typeof arr[0] === 'string') return arr; // already usernames
  return arr
    .map(x => x?.username || x?.user?.username || x)
    .filter(Boolean);
}, [userStats?.following]);



// Verified payment flag (P2P posts).
// NOTE: This is the same thing that used to power the
// "Verified Payments" tab — we just renamed the UI label to "P2P".
const hasVerifiedPaymentFlag = (post) => !!post?.transactionId;


const filterPosts = (post) => {
  switch (activeFilter) {
    case 'All':
      return true;

    case 'Following': {
      const author = getAuthorUsername(post);
      return followingUsernames.includes(author);
    }

    case 'Merchants':
      return isPaidActiveMerchant(post);

    case 'P2P': // UI label for verified-payment (transactionId) posts
      return hasVerifiedPaymentFlag(post);

    default:
      return true;
  }
};


// Always apply the filter client-side so only paid or trial merchants show up

const visiblePosts = React.useMemo(() => {
  return Array.isArray(posts) ? posts.filter(filterPosts) : [];
}, [posts, activeFilter, followingUsernames]);

const mixedFeedItems =
  React.useMemo(() => {
    if (
      activeFilter !== 'All'
    ) {
      return insertNewsIntoFeed(
        visiblePosts,
        []
      );
    }

    return insertNewsIntoFeed(
      visiblePosts,
      newsFeedArticles
    );
  }, [
    visiblePosts,
    newsFeedArticles,
    activeFilter,
  ]);


useEffect(() => {
  console.log(`[Home] mode=${feedMode} tab=${activeFilter} -> showing ${visiblePosts.length}/${posts.length}`);
}, [feedMode, activeFilter, posts, visiblePosts.length]);




// 🧭 Restore Home position before revealing the feed.
useEffect(() => {
  if (!homeIsActive) return;
  let restoreTimer = null;
  let firstFrame = null;
  let secondFrame = null;
  let revealFrame = null;

  try {
    const rawScrollTop =
      sessionStorage.getItem('bf_home_scrollTop');

    if (rawScrollTop == null) {
      setRestoringHomeScroll(false);
      return;
    }

    setRestoringHomeScroll(true);

    if (!Array.isArray(posts) || posts.length === 0) {
      return;
    }

    const returnSource =
      sessionStorage.getItem('bf_home_return_source') || '';

    const articleId =
      sessionStorage.getItem('bf_home_news_article_id') || '';

    /*
     * News cards are inserted separately from normal posts.
     * Wait until the News rotation is fully restored.
     */
    if (
      returnSource === 'news' &&
      activeFilter === 'All' &&
      newsFeedLoading
    ) {
      return;
    }

    if (
      returnSource === 'news' &&
      articleId &&
      !newsFeedArticles.some(
        (item) =>
          String(item?._id) ===
          String(articleId)
      )
    ) {
      return;
    }

    const savedScrollTop =
      parseInt(rawScrollTop, 10) || 0;

    const restorePosition = () => {
      firstFrame = requestAnimationFrame(() => {
        secondFrame = requestAnimationFrame(() => {
          const feed = feedRef.current;

          if (!feed) {
            setRestoringHomeScroll(false);
            return;
          }

          /*
           * Set both properties for better mobile-browser
           * reliability.
           */
          feed.scrollTop = savedScrollTop;

          feed.scrollTo({
            top: savedScrollTop,
            behavior: 'auto',
          });

          lastScrollTopRef.current =
            savedScrollTop;

          /*
           * Reveal Home only after the browser has applied
           * the restored position.
           */
          revealFrame = requestAnimationFrame(() => {
            setRestoringHomeScroll(false);
          });

          sessionStorage.removeItem(
            'bf_home_scrollTop'
          );

          sessionStorage.removeItem(
            'bf_home_fromPostId'
          );

          sessionStorage.removeItem(
            'bf_home_return_source'
          );

          sessionStorage.removeItem(
            'bf_home_news_article_id'
          );
        });
      });
    };

    restoreTimer = window.setTimeout(
      restorePosition,
      returnSource === 'news'
        ? 60
        : 0
    );
  } catch {
    setRestoringHomeScroll(false);
  }

  return () => {
    if (restoreTimer) {
      window.clearTimeout(restoreTimer);
    }

    if (firstFrame) {
      cancelAnimationFrame(firstFrame);
    }

    if (secondFrame) {
      cancelAnimationFrame(secondFrame);
    }

    if (revealFrame) {
      cancelAnimationFrame(revealFrame);
    }
  };
}, [
  homeIsActive,
  location.pathname,
  posts?.length,
  mixedFeedItems?.length,
  activeFilter,
  newsFeedLoading,
  newsFeedArticles,
]);




  // 🆕 --------- Discovery Row scroll persistence ----------
  const DISC_SCROLL_KEY = 'bf_discovery_scrollLeft';
  const DISC_INDEX_KEY = 'bf_discovery_clickIndex';

  // Save current horizontal scroll + index before navigating to a story/user
  const saveDiscoveryPos = (idx) => {
    const row = discoveryRowRef.current;
    const left = row?.scrollLeft || 0;
    try {
      sessionStorage.setItem(DISC_SCROLL_KEY, String(left));
      sessionStorage.setItem(DISC_INDEX_KEY, String(idx));
    } catch {}
  };

  // Restore horizontal scroll after we return to /home and bubbles are rendered
  const restoreDiscoveryPos = () => {
    const row = discoveryRowRef.current;
    if (!row) return;

    try {
      const rawLeft = sessionStorage.getItem(DISC_SCROLL_KEY);
      if (rawLeft == null) return;

      const left = parseInt(rawLeft, 10) || 0;

      // Use rAF twice to ensure layout is ready (avoids Safari hiccups)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          row.scrollLeft = left;
        });
      });

      // optional: clear after restore so we don’t override future manual scrolls
      sessionStorage.removeItem(DISC_SCROLL_KEY);
      sessionStorage.removeItem(DISC_INDEX_KEY);
    } catch {}
  };

  // When discovery users load/change and we’re on /home, attempt a restore
useEffect(() => {
  if (!homeIsActive) return;
  if (!discoveryUsers || discoveryUsers.length === 0) return;

  restoreDiscoveryPos();

  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [homeIsActive, location.pathname, discoveryUsers?.length]);

  // 🆕 --------- /Discovery Row scroll persistence ----------

// Always render with self first (even if context/cached list missed it)
const displayUsers = React.useMemo(() => {
  return ensureSelfFirst(discoveryUsers || [], user);
}, [discoveryUsers, user?._id, user?.username]);

// How many people (excluding self) are currently in the discovery row
const followingCountInRow = React.useMemo(() => {
  const arr = Array.isArray(displayUsers) ? displayUsers : [];
  return arr.filter(u => u?.username && u.username !== user.username).length;
}, [displayUsers, user.username]);


// Fetch suggested users only while Home is active.
useEffect(() => {
  if (!homeIsActive) return;
  if (followingCountInRow >= 20) return;

  if (!suggestedUsers || suggestedUsers.length === 0) {
    fetchSuggested({
      reset: true,
      limit: 12,
      keepPrevious: true,
    }).catch(() => {});

    return;
  }

  if (!isFresh()) {
    fetchSuggested({
      reset: true,
      limit: 12,
      keepPrevious: true,
      force: true,
    }).catch(() => {});
  }

  // Preserve the existing request cadence while active.
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [homeIsActive, followingCountInRow]);

// Merge following-only discovery users + suggested users (no duplicates)
const discoveryRowUsers = React.useMemo(() => {
  const base = Array.isArray(displayUsers) ? displayUsers : [];

  if (followingCountInRow >= 20) return base;

  const suggested = Array.isArray(suggestedUsers) ? suggestedUsers : [];

  const existing = new Set(
    base.map(u => String(u?.username || '').toLowerCase()).filter(Boolean)
  );

  const safeSuggested = suggested.filter(u => {
    const uname = String(u?.username || '').toLowerCase();
    if (!uname) return false;
    if (uname === String(user.username).toLowerCase()) return false;
    if (existing.has(uname)) return false;
    return true;
  });

  return [...base, ...safeSuggested];
}, [displayUsers, suggestedUsers, followingCountInRow, user.username]);


const shouldShowSuggestedSkeletons =
  followingCountInRow < 20 &&
  loadingSuggestedUsers &&
  (!suggestedUsers || suggestedUsers.length === 0);

const SUGGESTED_SKELETON_COUNT = 12;

const bottomNavProfileSrc = user?.profilePic || placeholderProfile;


return (
  <SuggestedProvider>
    <div className="home-container">
      {homeIsActive &&
        publishedPostToast &&
        publishedPostToast.username === user?.username && (
          <div className="home-published-toast">
            <div
              className="home-published-toast-message"
              role="status"
            >
              <span
                className="home-published-toast-check"
                aria-hidden="true"
              >
                ✓
              </span>
              <span>Post published</span>
            </div>

            <button
              type="button"
  

className="home-published-toast-button"
onClick={() => {
  const postId = publishedPostToast.postId;
  const publishedBy = publishedPostToast.username;

  setPublishedPostToast(null);

  openPostDetails(postId, {
    showBoostPromotion: true,
    publishedBy,
  });
}}

            >
              View post
            </button>
          </div>
        )}

      {restoringHomeScroll && (

        <div
          className="home-restore-loader"
          role="status"
          aria-label="Restoring your feed position"
        >
          <div className="home-restore-spinner" />
        </div>
      )}



<div
  className={`feed-content ${restoringHomeScroll ? 'is-restoring-scroll' : ''}`}
  ref={feedRef}

          onTouchStart={handlePullStart}
          onTouchMove={handlePullMove}
          onTouchEnd={handlePullEnd}
          onTouchCancel={handlePullEnd}
        >



<div className={`top-header-wrapper ${uiHidden ? 'is-hidden' : ''}`} ref={headerRef}>
  <div className="top-nav top-nav-main">

<div className="home-brand">
  <img src={logo} alt="BarterFi" className="home-brand-icon" />
  <span className="home-brand-text">BarterFi</span>
</div>



<div className="home-topbar-actions">
  <button
    type="button"
    className="home-notifications-btn"
    aria-label={
      unreadNotifCount > 0
        ? `Notifications, ${unreadNotifCount} unread`
        : 'Notifications'
    }
    onClick={() => {
      try {
        sessionStorage.setItem(
          'bf_home_scrollTop',
          String(feedRef.current?.scrollTop || 0)
        );
      } catch {}

      navigate('/notifications', {
        state: { from: 'home' },
      });
    }}
  >
    <img
      src={navIcons.notifications}
      alt=""
      className="home-notifications-icon"
    />

    {unreadNotifCount > 0 && (
      <span
        className="home-notifications-badge"
        aria-hidden="true"
      >
        {unreadNotifCount > 9 ? '9+' : unreadNotifCount}
      </span>
    )}
  </button>

  <div className="topbar-menu-wrapper">
    <img
      src={menuIcon}
      alt="Menu"
      className="topbar-menu-icon"
      onClick={() => setShowDrawer((v) => !v)}
    />
  </div>
</div>


  </div>



<div className="top-nav-tabs">
  <div className="segmented-tabs">
    <button
      type="button"
      className="segmented-tab active home-feed-tab"
      onClick={() => {
        console.log('SOCIAL FEED clicked');
        setFeedFilterOpen(true);
      }}
    >
      Social <span className="home-feed-chev">▾</span>
    </button>

    <button
      type="button"
      className="segmented-tab"
      onClick={() => navigate('/marketplace')}
    >
      Marketplace
    </button>

    <RouteSlider
      containerSelector=".segmented-tabs"
      activeSelector=".segmented-tab.active"
      storageKey="bf_top_pill_prev"
      currentRoute="home"
      isActive={homeIsActive}
    />
  </div>
</div>


</div>



{(pullDistance > 0 || pullRefreshing) && (
  <div
    className={`pull-reload-indicator ${pullReady ? 'is-ready' : ''} ${pullRefreshing ? 'is-refreshing' : ''}`}

style={{
  transform: `translateX(-50%) translateY(${Math.max(0, pullDistance)}px)`,
  opacity: pullRefreshing ? 1 : Math.min(1, pullDistance / 60),
}}

  >
    <div className="pull-reload-indicator-inner pull-reload-indicator-bf">


<BarterfiPullLoader
  progress={Math.min(1, pullDistance / PULL_TRIGGER)}
  spinning={pullRefreshing}
  size={30}
/>

    </div>
  </div>
)}


          <div className="discovery-wrapper">


            {discoveryError && (
              <div className="discovery-error">
                Couldn’t load stories. <button onClick={refetchDiscovery}>Retry</button>
              </div>
            )}





{displayUsers && displayUsers.length > 0 ? (
  <div className="discovery-row" ref={discoveryRowRef}>
   {discoveryRowUsers.map((u, idx) => {

      const isSelf = u.username === user.username;

const isSuggested = u?.suggested === true;

// ✅ Raw "has story in DB" (suggested users must NOT show story rings)
const hasDbStory = !isSuggested && (!!u.latestStory || u.hasStory === true);

      const latestTs = hasDbStory && u.latestStory
        ? u.latestStory.createdAt
        : null;

      let seen = false;
      if (hasDbStory && viewerUsername) {
        seen = hasSeenAll(viewerUsername, u.username, latestTs);
      }

      // 🧠 For this viewer:
      // - If they have a story AND I have NOT fully seen it → treat as active story.
      // - If I HAVE fully seen it → treat as “no active story” (normal bubble).
      const hasActiveStory = hasDbStory && !seen;

      const injectedSelf = isSelf && u._injected === true;


return (
  <StoryBubble
    key={u._id || u.username || idx}
    user={u}
    isCurrentUser={isSelf}
    isSuggested={isSuggested}
    hasDbStory={hasDbStory}
    hasActiveStory={hasActiveStory}
    seen={hasDbStory && seen}
    onClick={() => {
      console.log('🏠 [Home] STORY BUBBLE CLICK', {
        clicked: u.username,
        idx,
        isSelf,
        isSuggested,
        hasDbStory,
        injectedSelf,
      });

      saveDiscoveryPos(idx);

      if (isSuggested) {
        navigate(`/user/${u.username}`);
        return;
      }

      if (!isSelf) {
        navigate(`/stories/${u.username}`);
        return;
      }

      // ✅ SELF: ALWAYS open chooser drawer (drawer decides actions)
      console.log('🏠 [Home] OPEN SELF STORY DRAWER (always)', {
        hasDbStory,
        injectedSelf,
      });

      setSelfStoryHasDbStory(!!hasDbStory); // only allow View if DB has story
      setSelfStoryDrawerOpen(true);
    }}
  />
);



    })}

{shouldShowSuggestedSkeletons && (
  <>
    {Array.from({ length: SUGGESTED_SKELETON_COUNT }).map((_, i) => (
      <StoryBubbleSkeleton key={`suggested-skel-${i}`} />
    ))}
  </>
)}

  </div>


) : discoveryLoading ? (
  <div className="discovery-row" ref={discoveryRowRef}>
    <AvatarBubbleSkeleton count={12} />
  </div>
) : null}







          </div>




<div className="feed-container">




{loading && posts.length === 0 && (
  <div className="home-skeleton-wrapper">
    {Array.from({ length: 5 }).map((_, i) => (
      <SkeletonPost key={i} />
    ))}
  </div>
)}


  <>


{Array.isArray(mixedFeedItems) &&
  mixedFeedItems.map((feedItem) => {
    const {
      type,
      key,
      post,
      article,
      postIndex,
    } = feedItem;

    /* =====================================================
       NEWS DISCUSSION CARD
    ===================================================== */

if (type === 'news') {
  return (
    <div
      id={`home-news-${article?._id}`}
      key={key}
      className="feed-news-discussion-span"
    >
      <FeedNewsDiscussionCard
        article={article}
        user={user}
        onOpen={onOpenFeedNewsArticle}
      />
    </div>
  );
}

    /* =====================================================
       RISING NOW CARD
    ===================================================== */

    if (type === 'rising_now_card') {
      return (
        <React.Fragment key={key}>
          <div className="feed-rising-now-span">
            <RisingNowCard
              data={post.risingNowCard}
              onOpenLeaderboard={() => navigate('/badges-leaderboard')}
            />
          </div>
        </React.Fragment>
      );
    }

    /*
     * From this point onward, the item is a normal post.
     * postIndex counts only real posts and ignores News and
     * Rising Now cards.
     */

    const PAGE_SIZE = 10;

    const pageNumber =
      Math.floor(postIndex / PAGE_SIZE) + 1;

    const indexInPage =
      postIndex % PAGE_SIZE;

    // After post #3 on backend pages 2 and later.
    const shouldInsertStakeRow =
      pageNumber >= 2 &&
      indexInPage === 2;

    const stakePage =
      Math.max(1, pageNumber - 1);

    const stakeRowForThisPage =
      stakeRowsByPage[stakePage] || null;

    if (
      shouldInsertStakeRow &&
      stakeRowsByPage[stakePage] === undefined
    ) {
      fetchStakeRow(stakePage);
    }

    const baseLikes =
      post.likes?.length || 0;

    const baseComments =
      post.comments?.length || 0;

    const backendLiked =
      post.likes?.some(
        (like) =>
          like.username === user.username
      ) || false;

    let likedByMeLS = null;
    let commentBump = 0;

    try {
      const rawLike = localStorage.getItem(
        `bf_like_${post._id}_${user.username}`
      );

      if (rawLike !== null) {
        likedByMeLS = JSON.parse(rawLike);
      }
    } catch {}

    try {
      const rawCmt = localStorage.getItem(
        `bf_cmt_${post._id}_${user.username}`
      );

      if (rawCmt !== null) {
        commentBump =
          parseInt(rawCmt, 10) || 0;
      }
    } catch {}

    let likeDelta = 0;

    if (
      likedByMeLS === true &&
      !backendLiked
    ) {
      likeDelta = 1;
    }

    if (
      likedByMeLS === false &&
      backendLiked
    ) {
      likeDelta = -1;
    }

    const likesCount =
      Math.max(
        0,
        baseLikes + likeDelta
      );

    const commentsCount =
      Math.max(
        0,
        baseComments + commentBump
      );

    const userHasLiked =
      likedByMeLS === null
        ? backendLiked
        : likedByMeLS;

    return (
      <React.Fragment key={key}>
        <div
          id={`post-${post._id}`}
          className="home-feed-post-item"
        >
          {post.type === 'sponsored' ? (
            <SponsoredPostCard product={post} />
          ) : (
            <Post
              isActive={homeIsActive}
              id={post._id}
              name={post.name || post.user?.name}
              username={post.username || post.user?.username}
              profilePic={post.profilePic || post.user?.profilePic}
              location={post.location || post.user?.location}
              content={post.content}
              media={post.media}
              mediaType={post.mediaType}
              transactionId={post.transactionId}
              poll={post.poll || null}
              onPollVoteUpdate={handlePollVoteUpdate}
              timestamp={post.createdAt || post.timestamp}
              createdAt={post.createdAt || post.timestamp}
              updatedAt={post.updatedAt || null}
              editedAt={post.editedAt || null}
              editCount={Number(post.editCount || 0)}
              userType={getUserType(post)}
              isMerchant={isPaidActiveMerchant(post)}
              user={post.user}
              badgesBrief={getBadgesBrief(post)}

              currentUser={user.username}
              currentUserProfilePic={user?.profilePic}
              currentScreen="home"
              onDelete={() => handleDeletePost(post._id)}
              closeSignal={closePostMenusSignal}

              likesCount={likesCount}
              commentsCount={commentsCount}
              viewCount={Number(post.viewCount || 0)}
              userHasLiked={userHasLiked}

              thumbnail={post.thumbnail || ''}
              mentions={post.mentions || []}
              mentionMap={post.mentionMap || {}}
              postIndex={postIndex}
              pinned={post.pinned === true}
              stakeActivePi={Number(post.stakeActivePi || 0)}
              stakeNextExpiryAt={post.stakeNextExpiryAt || null}
              onStakeDetailsClick={() => openStakeDetails(post._id)}
              isRepost={post.isRepost === true || post.type === 'repost'}
              repostOf={post.repostOf}
              repostComment={post.repostComment || ''}
              postType={post.postType || 'post'}
              sharedProduct={post.sharedProduct || null}
              productSnapshot={post.productSnapshot || null}
              style={post.style || null}
              backgroundTheme={
                post.backgroundTheme ||
                post.style?.backgroundTheme ||
                'normal'
              }
              hasPaidStyle={
                post.hasPaidStyle === true ||
                post.style?.paid === true
              }
              postAction={post.postAction || null}
            />
          )}
        </div>

        {shouldInsertStakeRow &&
          stakeRowForThisPage && (
            <div className="feed-stake-row-span">
              <StakeRow
                key={`stake-row-${stakePage}`}
                row={stakeRowForThisPage}
                onOpenPost={openPostDetails}
              />
            </div>
          )}

        {postIndex === NEW_LISTINGS_INSERT_INDEX &&
          activeFilter === 'All' &&
          visiblePosts.length > NEW_LISTINGS_INSERT_INDEX && (
            <div className="feed-new-listings-span">
              <NewListingsRow
                user={user}
                onOpen={onOpenNewListingProduct}
                onViewAll={handleViewAllNewListings}
              />
            </div>
          )}


{postIndex === COMMUNITY_AVATARS_INSERT_INDEX &&
  activeFilter === 'All' &&
  visiblePosts.length > COMMUNITY_AVATARS_INSERT_INDEX && (
    <div className="feed-community-avatars-span">

<CommunityAvatarsRow
  user={user}
  isActive={homeIsActive}
  onCreateAvatar={handleCreateCommunityAvatar}
  onOpenAvatar={handleOpenCommunityAvatar}
  onViewAll={handleViewAllCommunityAvatars}
/>
    </div>
  )}


        {postIndex === SUGGESTED_PIONEERS_INSERT_INDEX &&
          activeFilter === 'All' &&
          visiblePosts.length > SUGGESTED_PIONEERS_INSERT_INDEX && (
            <div className="feed-suggested-pioneers-span">
              <SuggestedPioneersRow
                user={user}
                onOpenProfile={onOpenSuggestedPioneer}
                onSeeAll={handleViewAllSuggestedPioneers}
              />
            </div>
          )}

        {postIndex === SUGGESTED_INSERT_INDEX &&
          visiblePosts.length > SUGGESTED_INSERT_INDEX && (
            <div className="feed-suggested-span">
              <SuggestedRow
                onOpen={onOpenSuggestedProduct}
              />
            </div>
          )}
      </React.Fragment>
    );
  })}




    {loading && posts.length > 0 && hasMore && (
      <div className="feed-bottom-spinner" aria-label="Loading more" />
    )}
  </>
</div>



          {!loading && !hasMore && (
            <div className="end-of-feed-msg">
              <img src={scrollToTopIcon} alt="Scroll to top" className="end-scroll-icon" />
              <p>You've reached the end. Tap the button to explore again!</p>
            </div>
          )}
        </div>





<BottomNav
  user={user}
  profileSrc={bottomNavProfileSrc}
  unreadDmCount={unreadDmCount}
  hidden={!homeIsActive || uiHidden}
  onBeforeNavigate={() => {
    try {
      sessionStorage.setItem(
        'bf_home_scrollTop',
        String(feedRef.current?.scrollTop || 0)
      );
    } catch {}
  }}
/>





{showPremiumHighlightsPill && (

<div
  className={`badge-pill-floating ${uiHidden ? 'is-raised' : ''}`}
  onClick={handlePremiumPillClick}
>
    <div className="badge-pill-arrow">
      <img src={pillScrollToTopIcon} alt="New posts" />
    </div>

    <div className="badge-pill-avatars">
      {(premiumSummary?.previewUsers || []).slice(0, 3).map((u, i) => (
        <div key={`${u.username || i}`} className="badge-pill-avatar">
          <img
            src={u.profilePic || '/placeholder-profile.png'}
            alt={u.username || 'premium user'}
            style={{ objectFit: 'cover' }}
          />
        </div>
      ))}
    </div>

<div className="badge-pill-text">
  Premium+
</div>

  </div>
)}


{showBadgeHighlightsPill && (
 
<div
  className={`badge-pill-floating ${uiHidden ? 'is-raised' : ''}`}
  onClick={handleBadgePillClick}
>

    <div className="badge-pill-arrow">
      <img src={pillScrollToTopIcon} alt="New posts" />
    </div>

    <div className="badge-pill-avatars">
      {HIGHLIGHT_BADGE_IDS
        .map((id) => BADGE_DISPLAY[id])
        .filter(Boolean)
        .slice(0, 3)
        .map((badge) => (
          <div key={badge.id} className="badge-pill-avatar">
            <img src={badge.icon} alt={badge.label} />
          </div>
        ))}
    </div>




  </div>
)}



{showNewHighlightsPill && (

<div
  className={`badge-pill-floating ${uiHidden ? 'is-raised' : ''}`}
  onClick={handleNewPillClick}
>
    <div className="badge-pill-arrow">
      <img src={pillScrollToTopIcon} alt="New posts" />
    </div>

    <div className="badge-pill-text">
      New Post
    </div>
  </div>
)}


{showRisingNowHighlightsPill && (
  <div
    className={`badge-pill-floating ${uiHidden ? 'is-raised' : ''}`}
    onClick={handleRisingNowPillClick}
  >
    <div className="badge-pill-arrow">
      <img src={pillScrollToTopIcon} alt="Rising now" />
    </div>

    <div className="badge-pill-text">
      Rising Now
    </div>
  </div>
)}




{user?.username && !showPioneerExplorer && !feedFilterOpen && (
  <button
    type="button"
    className={`home-summary-fab ${
      showPioneerFabLabel && !uiHidden ? 'is-expanded' : ''
    } ${
      showScrollTop && !uiHidden ? 'above-scroll-top' : ''
    } ${uiHidden ? 'is-hidden' : ''}`}
    onClick={() => {
      setShowPioneerFabLabel(false);
      setShowPioneerExplorer(true);
    }}
    aria-label="Explore Pioneers"
    title="Explore Pioneers"
  >
    <span className="home-summary-fab-label">
      Explore Pioneers
    </span>

    <span className="home-summary-fab-icon">
      <img src={pioneerSummaryIcon} alt="" />
    </span>
  </button>
)}



        {showScrollTop && (

            <div className={`scroll-to-top ${uiHidden ? 'is-hidden' : ''}`} onClick={scrollToTop}>
            <img src={scrollToTopIcon} alt="Scroll to top" />
          </div>
        )}







<FeedFilterDrawer
  open={feedFilterOpen}
  onClose={() => setFeedFilterOpen(false)}
  activeFilter={activeFilter}
  onSelect={async (filter) => {
    setFeedFilterOpen(false);

    setActiveFilter(filter);
    const mode = labelToMode(filter);

setBadgeHighlightsInjected(false);
setNewHighlightsInjected(false);
setPremiumHighlightsInjected(false);
setRisingHighlightsInjected(false);

setHighlightsGate(null);
setBadgeSummary(null);
setNewSummary(null);
setPremiumSummary(null);
setRisingNowSummary(null);
setRisingNowCard(null);
setSilentInsertedPill(null);

    clearPostState();
    await loadInitialPosts(user.username, true, mode);

    if (feedRef.current) {
      feedRef.current.scrollTo({ top: 0, behavior: 'instant' });
    }
  }}
/>



<SelfStoryDrawer
  open={selfStoryDrawerOpen}
  onClose={() => setSelfStoryDrawerOpen(false)}
  username={user?.username || ''}
  canView={selfStoryHasDbStory}
  onView={() => {
    setSelfStoryDrawerOpen(false);
    navigate(`/stories/${user.username}`);
  }}
  onCreate={() => {
    setSelfStoryDrawerOpen(false);
    navigate('/create-story');
  }}
/>





<SideDrawer
  open={showDrawer}
  onClose={() => setShowDrawer(false)}
  user={user}
  switchTarget={switchTarget}
  onSwitchIdentity={switchTarget ? () => handleSwitchIdentity(switchTarget) : null}
  switchingIdentity={switchingIdentity}
  showAccountsIcon={shouldShowAccountsIcon}
  onOpenAccountsDrawer={() => setAccountsDrawerOpen(true)}
  accountsIcon={accountsIcon}
>

  <div className="homedrawer-list">
    <div className="homedrawer-item" style={{ paddingTop: 4, paddingBottom: 8 }}>
      <LanguageSwitcher compact showLabel={false} />
    </div>

    {user.userType === 'admin' && (
      <>
        <div className="homedrawer-section-title">{t('nav.admin')}</div>

        <div
          className="homedrawer-item"
          onClick={() => { setShowDrawer(false); navigate('/admin'); }}
        >
          <img src={adminIcon} alt="" />
          <div className="homedrawer-item-label">{t('Admin_dashboard')}</div>
        </div>

        <div className="homedrawer-divider"></div>
      </>
    )}

    {canSeePlans && (
      <>
        <div className="homedrawer-section-title">{t('drawer.premium')}</div>

        <div
          className="homedrawer-item"
          onClick={() => { setShowDrawer(false); navigate('/plans'); }}
        >
          <img src={plansIcon} alt="" />
          <div className="homedrawer-item-label">{t('drawer.plans')}</div>
        </div>

        <div
          className="homedrawer-item"
          onClick={() => {
            setShowDrawer(false);
            navigate('/analytics', { state: { from: 'home' } });
          }}
        >
          <img src={dashboardIcon} alt="" />
          <div className="homedrawer-item-label">{t('drawer.dashboard')}</div>
        </div>

        <div className="homedrawer-divider"></div>
      </>
    )}

    <div className="homedrawer-section-title">{t('nav.my_stuff')}</div>


<div
  className="homedrawer-item"
  onClick={() => { setShowDrawer(false); navigate('/my-apps'); }}
>
  <img src={globeIcon} alt="" />
  <div className="homedrawer-item-label">My Apps</div>
</div>


    <div
      className="homedrawer-item"
      onClick={() => { setShowDrawer(false); navigate('/my-wallet'); }}
    >
      <img src={mywalletIcon} alt="" />
      <div className="homedrawer-item-label">{t('nav.wallet')}</div>
    </div>

    <div
      className="homedrawer-item"
      onClick={() => { setShowDrawer(false); navigate('/orders'); }}
    >
      <img src={ordersIcon} alt="" />
      <div className="homedrawer-item-label">{t('drawer.my_orders')}</div>
    </div>

    <div
      className="homedrawer-item"
      onClick={() => { setShowDrawer(false); navigate('/saved'); }}
    >
      <img src={savedIcon} alt="" />
      <div className="homedrawer-item-label">{t('drawer.saved_posts')}</div>
    </div>

    <div
      className="homedrawer-item"
      onClick={() => { setShowDrawer(false); navigate('/saved-products'); }}
    >
      <img src={saveIconFilled} alt="" />
      <div className="homedrawer-item-label">{t('drawer.saved_products')}</div>
    </div>

    <div className="homedrawer-divider"></div>

    <div className="homedrawer-section-title">{t('drawer.logs_leaderboard')}</div>

    <div
      className="homedrawer-item"
      onClick={() => { setShowDrawer(false); navigate('/receipts'); }}
    >
      <img src={receiptLogIcon} alt="" />
      <div className="homedrawer-item-label">{t('drawer.receipt_log')}</div>
    </div>

    <div
      className="homedrawer-item"
      onClick={() => { setShowDrawer(false); navigate('/badges-leaderboard'); }}
    >
      <img src={leaderboardIcon} alt="" />
      <div className="homedrawer-item-label">{t('drawer.badges_leaderboard')}</div>
    </div>

    <div className="homedrawer-divider"></div>

    <div className="homedrawer-section-title">{t('nav.help_info')}</div>

    <div
      className="homedrawer-item"
      onClick={() => { setShowDrawer(false); navigate('/feedback'); }}
    >
      <img src={feedbackIcon} alt="" />
      <div className="homedrawer-item-label">{t('drawer.feedback')}</div>
    </div>

    <div
      className="homedrawer-item"
      onClick={() => { setShowDrawer(false); navigate('/about-barterfi'); }}
    >
      <img src={aboutIcon} alt="" />
      <div className="homedrawer-item-label">{t('drawer.about_barterfi')}</div>
    </div>

    <div
      className="homedrawer-item"
      onClick={() => { setShowDrawer(false); navigate('/terms'); }}
    >
      <img src={termsIcon} alt="" />
      <div className="homedrawer-item-label">{t('legal.terms')}</div>
    </div>

    <div className="homedrawer-divider"></div>

    <div
      className="homedrawer-item"
      onClick={() => {
        setShowDrawer(false);
        navigate('/profile');
      }}
    >
      <img src={navIcons.profile} alt="" />
      <div className="homedrawer-item-label">{t('nav.profile')}</div>
    </div>

    <div className="homedrawer-divider" />

    <div className="homedrawer-section-title">{t('drawer.themes')}</div>

    <div
      className="homedrawer-item theme-toggle-row"
      onClick={toggleTheme}
    >
      <div className="homedrawer-item-label">
        {t('drawer.theme_label')}: {theme === 'dark' ? t('drawer.dark') : t('drawer.light')}
      </div>

      <div className={`theme-toggle-switch ${theme === 'dark' ? 'on' : 'off'}`}>
        <div className="theme-toggle-knob" />
      </div>
    </div>
  </div>
</SideDrawer>



<AccountsDrawer
  open={accountsDrawerOpen}
  onClose={() => setAccountsDrawerOpen(false)}
  currentAccount={currentIdentity}
  accounts={[currentIdentity, ...orderedOtherAccounts]}
  onSwitchAccount={(account) => {
    if (account.id === currentIdentity.id) return;
    handleSwitchIdentity(account);
  }}
/>


<StakeDetailsDrawer
  isOpen={stakeDetailsOpen}
  postId={stakeDetailsPostId}
  onClose={() => setStakeDetailsOpen(false)}
/>

{showPioneerExplorer && user?.username && (
  <HomePioneerExplorer
    user={user}
    onClose={() => setShowPioneerExplorer(false)}
  />
)}

{communityAvatarViewerOpen && (
  <CommunityAvatarViewer
    key={communityAvatarSelection.creationId}
    creationId={communityAvatarSelection.creationId}
    onClose={handleCloseCommunityAvatar}
    onCreateAvatar={handleCreateCommunityAvatar}
    onOpenProfile={(username) => {
      setCommunityAvatarSelection(null);

      if (username === user?.username) {
        const y = feedRef.current?.scrollTop || 0;

        try {
          sessionStorage.setItem('bf_home_scrollTop', String(y));
        } catch {}

        navigate('/profile', {
          state: {
            from: 'home',
            restoreHomeScroll: true,
          },
        });

        return;
      }

      onOpenSuggestedPioneer(username);
    }}
  />
)}



{homeIsActive && (
  <HomeAwarenessManager
    user={user}
    onUserUpdate={onUserUpdate}
    blocked={
      communityAvatarViewerOpen ||
      showPioneerExplorer ||
      showDrawer ||
      accountsDrawerOpen ||
      feedFilterOpen ||
      selfStoryDrawerOpen ||
      stakeDetailsOpen ||
      switchingIdentity
    }
  />
)}


      </div>
    </SuggestedProvider>
  );
}

export default Home;
